import {
  CarWheelMeasurementType,
  InspectionContext,
  InspectionItemKind,
  type CarWheelPositionText,
  type InspectionItemCreateInput,
  type InspectionItemRecord,
  type TireSeason
} from '@navio/nsdk';

import { datesSortCompare } from '../date/date';
import { INSPECTION_WHEELS, wheelFieldFromLocation, type WheelField, type WheelLocation } from './wheelPositions';

/** Where an observation sits on the subject: a wheel position, or a vehicle side. */
type ObservationLocation = NonNullable<InspectionItemRecord['location']>;

/**
 * The fields the resolver reads. Session records satisfy this; tests can pass a stub.
 * A later reading wins per wheel, independently for tread, photo and specification.
 */
export type ObservationSession = {
  id: number;
  inspectedAt: string;
  context: InspectionContext;
  skipReason?: string | null;
  items?: ObservationItem[] | null;
};

export type ObservationItem = {
  id: number;
  kind?: InspectionItemKind | null;
  location?: ObservationLocation | null;
  measurementType?: CarWheelMeasurementType | null;
  imageUrl?: string | null;
  skipReason?: string | null;
  value?: { value: number; unit: string } | null;
  tire?: { season?: TireSeason | null; specification?: string | null; carWheelSetId?: number | null } | null;
  createdAt?: string | null;
};

type ObservationFilter = {
  /** An item without a season stays in. */
  season?: TireSeason;
  /**
   * One session can cover every set on the car, so only items pinned to this set stay in. An unpinned item
   * has no physical wheel behind it and could belong to any set.
   */
  carWheelSetId?: number;
};

type ResolvedTread = {
  itemId: number;
  sessionId: number;
  value: number;
  unit: string;
  measuredAt: string;
  season: TireSeason | null;
};

export type ResolvedWheel = {
  field: WheelField;
  location: WheelLocation;
  /** Newest usable tread. A later skipped reading does not replace it. */
  tread: ResolvedTread | null;
  /** Set when the newest tread item was skipped. A later usable reading clears it. */
  unreadable: { itemId: number; sessionId: number; reason: string } | null;
  /** The item a correction patches: the newest tread item, skipped or not. */
  treadItemId: number | null;
  photo: { itemId: number; sessionId: number; imageUrl: string; measuredAt: string } | null;
  specification: ResolvedSpecification | null;
};

export type ResolvedSpecification = {
  location: ObservationLocation;
  specification: string;
  measuredAt: string;
  sessionId: number;
  itemId: number;
};

export type ResolvedObservations<T extends ObservationSession = ObservationSession> = {
  wheels: ResolvedWheel[];
  /** One winning specification per location, including axle-level FRONT / REAR. */
  specifications: ResolvedSpecification[];
  /** Newest usable tread per season per wheel. A summer reading does not drop the winter one. */
  treadsBySeason: Partial<Record<TireSeason, Partial<Record<WheelField, ResolvedTread>>>>;
  season: TireSeason | null;
  /** Set only when the sessions observed nothing. An empty skip does not hide a reading. */
  skipReason: string | null;
  /** Session a card should attribute: the one that recorded the newest observation, else the skip. */
  session: T | null;
  /** Every session in this resolution, newest first. */
  sessionIds: number[];
};

export type JobStageKey = 'preJob' | 'postJob';

/**
 * The two job stages, in the order they are shown. The single source for a stage's key,
 * its wording and the context it reads, so a label never drifts between screens.
 */
export const JOB_STAGES = [
  { key: 'preJob', label: 'Pre job', context: InspectionContext.PRE_JOB },
  { key: 'postJob', label: 'Post job', context: InspectionContext.POST_JOB }
] as const satisfies readonly { key: JobStageKey; label: string; context: InspectionContext }[];

export type JobStage = (typeof JOB_STAGES)[number];

export type ResolvedJobStage<T extends ObservationSession = ObservationSession> = ResolvedObservations<T> & JobStage;

const byTime =
  (sort: 'asc' | 'desc') =>
  <T extends { inspectedAt: string; id: number }>(left: T, right: T) =>
    datesSortCompare(left.inspectedAt, right.inspectedAt, sort) ||
    (sort === 'asc' ? left.id - right.id : right.id - left.id);

const isTreadItem = (item: ObservationItem) =>
  item.kind === InspectionItemKind.MEASUREMENT && item.measurementType === CarWheelMeasurementType.TREAD_DEPTH;

const isUsableTread = (item: ObservationItem) => isTreadItem(item) && !item.skipReason && item.value != null;

type Walk = {
  wheels: ResolvedWheel[];
  specifications: Map<ObservationLocation, ResolvedSpecification>;
  treadsBySeason: ResolvedObservations['treadsBySeason'];
  season: TireSeason | null;
  contributor: ObservationSession | null;
};

const emptyWalk = (): Walk => ({
  wheels: INSPECTION_WHEELS.map(({ field, location }) => ({
    field,
    location,
    tread: null,
    unreadable: null,
    treadItemId: null,
    photo: null,
    specification: null
  })),
  specifications: new Map(),
  treadsBySeason: {},
  season: null,
  contributor: null
});

const isFilteredOut = (item: ObservationItem, filter: ObservationFilter | undefined) => {
  const tire = item.tire;
  if (filter?.season && tire?.season && tire.season !== filter.season) {
    return true;
  }
  return filter?.carWheelSetId != null && tire?.carWheelSetId !== filter.carWheelSetId;
};

const applyItem = (walk: Walk, item: ObservationItem, session: ObservationSession, filter?: ObservationFilter) => {
  if (isFilteredOut(item, filter)) {
    return;
  }
  const measuredAt = item.createdAt ?? session.inspectedAt;
  const field = wheelFieldFromLocation(item.location);
  const wheel = field ? walk.wheels.find((candidate) => candidate.field === field) : undefined;

  const treadApplied = applyTread(walk, wheel, item, session);
  const photoApplied = applyPhoto(wheel, item, measuredAt, session.id);
  const specificationApplied = applySpecification(walk, wheel, item, measuredAt, session.id);

  if (treadApplied || photoApplied || specificationApplied) {
    walk.contributor = session;
    if (item.tire?.season) {
      walk.season = item.tire.season;
    }
  }
};

const applyTread = (
  walk: Walk,
  wheel: ResolvedWheel | undefined,
  item: ObservationItem,
  session: ObservationSession
) => {
  if (!wheel || !isTreadItem(item)) {
    return false;
  }
  wheel.treadItemId = item.id;
  if (isUsableTread(item) && item.value) {
    const tread: ResolvedTread = {
      itemId: item.id,
      sessionId: session.id,
      value: item.value.value,
      unit: item.value.unit,
      // Imported reading rows can be created months after the inspection.
      measuredAt: session.inspectedAt,
      season: item.tire?.season ?? null
    };
    wheel.tread = tread;
    wheel.unreadable = null;
    const season = item.tire?.season;
    if (season) {
      walk.treadsBySeason[season] = { ...walk.treadsBySeason[season], [wheel.field]: tread };
    }
  } else if (item.skipReason) {
    wheel.unreadable = { itemId: item.id, sessionId: session.id, reason: item.skipReason };
  }
  return true;
};

const applyPhoto = (wheel: ResolvedWheel | undefined, item: ObservationItem, measuredAt: string, sessionId: number) => {
  if (!wheel || !item.imageUrl) {
    return false;
  }
  wheel.photo = { itemId: item.id, sessionId, imageUrl: item.imageUrl, measuredAt };
  return true;
};

const applySpecification = (
  walk: Walk,
  wheel: ResolvedWheel | undefined,
  item: ObservationItem,
  measuredAt: string,
  sessionId: number
) => {
  const specification = item.tire?.specification;
  if (!item.location || !specification) {
    return false;
  }
  const resolved = { location: item.location, specification, measuredAt, sessionId, itemId: item.id };
  walk.specifications.set(item.location, resolved);
  if (wheel) {
    wheel.specification = resolved;
  }
  return true;
};

const orderedItems = (items: ObservationItem[]) =>
  [...items].sort((left, right) => datesSortCompare(left.createdAt, right.createdAt, 'asc') || left.id - right.id);

/** Newest observation per wheel across the sessions given. */
export const resolveObservations = <T extends ObservationSession>(
  sessions: T[],
  filter?: ObservationFilter
): ResolvedObservations<T> => {
  const walk = emptyWalk();
  const ordered = [...sessions].sort(byTime('asc'));
  for (const session of ordered) {
    for (const item of orderedItems(session.items ?? [])) {
      applyItem(walk, item, session, filter);
    }
  }

  const specifications = [...walk.specifications.values()];
  const hasObservation =
    specifications.length > 0 || walk.wheels.some((wheel) => wheel.tread || wheel.unreadable || wheel.photo);
  const newestFirst = [...sessions].sort(byTime('desc'));
  const skipSession = newestFirst.find((session) => session.skipReason);

  return {
    wheels: walk.wheels,
    specifications,
    treadsBySeason: walk.treadsBySeason,
    season: walk.season,
    skipReason: hasObservation ? null : (skipSession?.skipReason ?? null),
    session: (hasObservation ? walk.contributor : (skipSession ?? newestFirst[0] ?? null)) as T | null,
    sessionIds: newestFirst.map((session) => session.id)
  };
};

/** Photos and specifications belong to their stage. Job treads are shared by tire season. */
export const resolveJobStages = <T extends ObservationSession>(sessions: T[]): ResolvedJobStage<T>[] =>
  JOB_STAGES.flatMap<ResolvedJobStage<T>>((stage) => {
    const matching = sessions.filter((session) => session.context === stage.context);
    if (matching.length === 0) {
      return [];
    }
    const resolved = resolveObservations(matching);
    const photographedSeason = resolveObservations(
      matching.map((session) => ({
        ...session,
        items: (session.items ?? []).filter((item) => item.kind !== 'MEASUREMENT')
      }))
    ).season;
    const season = photographedSeason ?? resolved.season;
    if (season == null) {
      return [{ ...stage, ...resolved }];
    }
    // Installed workers and historical imports recorded job treads without a stage.
    // New workers measure both seasons on PRE_JOB. Share only the readings; a stage
    // void must keep owning only its own sessions, not another stage's observations.
    const measurements = resolveObservations(
      sessions.map((session) => ({
        ...session,
        items: (session.items ?? []).filter((item) => item.kind === 'MEASUREMENT')
      })),
      { season }
    );
    return [
      {
        ...stage,
        ...resolved,
        season,
        wheels: resolved.wheels.map((wheel) => {
          const measured = measurements.wheels.find((candidate) => candidate.field === wheel.field)!;
          return {
            ...wheel,
            tread: measured.tread,
            unreadable: measured.unreadable,
            treadItemId: measured.treadItemId
          };
        }),
        treadsBySeason: measurements.treadsBySeason
      }
    ];
  });

export const newestSpecification = (specifications: ResolvedSpecification[]) =>
  [...specifications].sort(
    (left, right) => datesSortCompare(left.measuredAt, right.measuredAt, 'desc') || right.itemId - left.itemId
  )[0];

export const treadMeasurementItem = ({
  location,
  season,
  value
}: {
  location: CarWheelPositionText;
  season: TireSeason;
  value: { value: number; unit: string };
}): InspectionItemCreateInput => ({
  kind: 'MEASUREMENT',
  location,
  value,
  measurementType: 'TREAD_DEPTH',
  tire: { season }
});
